import { CheckIcon, ClipboardDocumentIcon } from "@heroicons/react/16/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useContext, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../externalComponents/tailwind/dialog";
import { OverflowFrame } from "../../../externalComponents";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { Button } from "../../../externalComponents/tailwind/button";
import { Divider } from "../../../externalComponents/tailwind/divider";
import { Field, Legend } from "../../../externalComponents/tailwind/fieldset";
import { Heading } from "../../../externalComponents/tailwind/heading";
import { Input } from "../../../externalComponents/tailwind/input";
import { Text } from "../../../externalComponents/tailwind/text";
import { cleanPhone } from "../../../functions/cleanPhone";
import { CustomerContext } from "../../../providers";
import { useCompanyUsers } from "./hooks";

export default function GeneralUsersPage() {
  const { user, customer } = useContext(CustomerContext);
  const { users, isLoading, saveUsersSettings, updateUser, deleteUser, isDeleting } = useCompanyUsers();
  const [copied, setCopied] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const copyInvitationLink = async () => {
    const invitationUrl = `${window.location.origin}/dashboard?invitation_code=${user.customer.codeForLogin}&company_name=${encodeURIComponent(user.customer.name)}`;
    try {
      await navigator.clipboard.writeText(invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

  return (
    <>
      <SettingsSidebar>
        <PageHeading
          title="Users"
          primaryAction="Save"
          handlePrimaryAction={saveUsersSettings}
          isPrimaryLoading={isLoading}
        />
        <OverflowFrame>
          {user?.customer?.codeForLogin && (
            <>
              {!customer.azureTenantId && (
                <div className="my-5">
                  <Heading className="text-lg">Invite users by link</Heading>
                  <div className="flex items-center gap-3">
                    <Text className="flex-1">
                      {window.location.origin}/dashboard?company_name=
                      {encodeURIComponent(user.customer.name)}&invitation_code=
                      {user.customer.codeForLogin}
                      <Button
                        color="primaryButton"
                        onClick={copyInvitationLink}
                        className="ml-2"
                      >
                        {copied ? (
                          <CheckIcon className="w-4 h-4" />
                        ) : (
                          <ClipboardDocumentIcon className="w-4 h-4" />
                        )}
                        {copied ? "Copied!" : "Copy link"}
                      </Button>
                    </Text>
                  </div>
                </div>
              )}
              <Divider />
            </>
          )}
          <div className="my-5">
            <Heading className="text-lg">Company users</Heading>
            <p
              style={{
                fontSize: "0.75rem",
                color: "#999",
                marginTop: "0.25rem",
              }}
            >
              The phone number must include your country code (e.g., 33601020304)
            </p>{" "}
            {users &&
              customer?.id !== "1138" &&
              users.map((companyUser, index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-5 items-end">
                  <Field>
                    <Legend>Email</Legend>
                    <Input
                      name="Email"
                      value={companyUser.email}
                      disabled
                      autoComplete="off"
                      maxLength={200}
                    />
                  </Field>
                  <Field>
                    <Legend>First Name</Legend>
                    <Input
                      name="First Name"
                      value={companyUser.firstName}
                      onChange={(e) =>
                        updateUser(companyUser.email, "firstName", e.target.value)
                      }
                      autoComplete="off"
                      maxLength={32}
                    />
                  </Field>
                  <Field>
                    <Legend>Last Name</Legend>
                    <Input
                      name="Last Name"
                      value={companyUser.lastName}
                      onChange={(e) =>
                        updateUser(companyUser.email, "lastName", e.target.value)
                      }
                      autoComplete="off"
                      maxLength={32}
                    />
                  </Field>
                  <Field>
                    <Legend>Phone</Legend>
                    <Input
                      name="Phone"
                      value={companyUser.phone || ""}
                      onChange={(e) => {
                        updateUser(
                          companyUser.email,
                          "phone",
                          cleanPhone(e.target.value.replace(/[^0-9]/g, "")),
                        );
                      }}
                      autoComplete="off"
                      maxLength={15}
                    />
                  </Field>
                  <div className="flex items-center justify-center pb-2">
                    {user?.id !== companyUser.id && (
                      <button
                        onClick={() => {
                          setUserToDelete(companyUser);
                          setIsModalOpen(true);
                        }}
                        className="rounded hover:bg-gray-100 hover:cursor-pointer"
                      >
                        <TrashIcon className="w-5 h-5 text-gray-500" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </OverflowFrame>
      </SettingsSidebar>
      <Dialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setUserToDelete(null);
        }}
      >
        <DialogTitle>Delete user</DialogTitle>
        <DialogBody>
          <DialogDescription>
            Are you sure you want to delete {userToDelete?.firstName || ""} {userToDelete?.lastName || ""} ({userToDelete?.email})?
            <br />
            <strong className="font-semibold text-red-600">This action is irreversible.</strong>
          </DialogDescription>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => {
              setIsModalOpen(false);
              setUserToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => {
              if (userToDelete) {
                deleteUser(userToDelete.id);
              }
              setIsModalOpen(false);
              setUserToDelete(null);
            }}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
